import { useState, useEffect, useCallback } from "react";
import {
  Search,
  Filter,
  Eye,
  Calendar,
  User,
  Mail,
  Phone,
  Briefcase,
  Award,
  Building,
  Download,
  Home,
  DollarSign,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import Loader from "../../../common_components/Loader";
import DashboardStats from "../../faculty/components/DashboardStats";

interface ApplicationDashboardPageProps {
  type: "FACULTY" | "NON_FACULTY";
}

export default function ApplicationDashboardPage({ type }: ApplicationDashboardPageProps) {
  const { recruitmentId } = useParams();
  const [applications, setApplications] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const { apiRequest, handleDownload } = useAuth();
  const navigate = useNavigate();

  const isFaculty = type === "FACULTY";
  const prefix = isFaculty ? "/faculty" : "/non-faculty";
  const title = isFaculty ? "Faculty Applications Dashboard" : "Non-Faculty Applications Dashboard";

  const [filters, setFilters] = useState({
    q: "",
    candidateType: "",
    categoryOfPost: "",
    categoryOfCandidate: "",
    department: "",
    paymentStatus: "",
    status: "",
    dateFrom: "",
    dateTo: "",
    pwd: "",
    gender: "",
    isGovernmentServant: "",
    experienceYearsMin: "",
    experienceYearsMax: "",
    state: "",
    maritalStatus: "",
    page: 1,
    limit: 10,
    sort: "-createdAt",
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
  });

  const fetchDashboardStats = useCallback(async () => {
    try {
      const data = await apiRequest(
        `${prefix}/application/admin/dashboard-stats?recruitmentId=${recruitmentId}`
      );
      setStats(data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, [apiRequest, prefix, recruitmentId]);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { recruitmentId };
      for (const key in filters) {
        if ((filters as any)[key] !== "" && (filters as any)[key] !== null) {
          params[key] = (filters as any)[key];
        }
      }

      const queryString = new URLSearchParams(params as any).toString();
      const url = `${prefix}/application/admin/all?${queryString}`;

      const response = await apiRequest(url);
      setApplications(response.applications || []);
      setPagination({
        currentPage: response.currentPage || 1,
        totalPages: response.totalPages || 1,
        total: response.total || 0,
      });
    } catch (error) {
      console.error("Error fetching applications:", error);
    } finally {
      setLoading(false);
    }
  }, [apiRequest, filters, prefix, recruitmentId]);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchApplications();
    }, 400);

    return () => clearTimeout(handler);
  }, [fetchApplications]);

  const handleFilterChange = (key: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key !== "page" ? 1 : value,
    }));
  };

  const getStatusColor = (status: string) => {
    const s = status ? status.toUpperCase() : "";
    const colors: Record<string, string> = {
      DRAFT: "bg-yellow-100 text-yellow-800 border border-yellow-200",
      SUBMITTED: "bg-blue-100 text-blue-800 border border-blue-200",
      ACCEPTED: "bg-green-100 text-green-800 border border-green-200",
      REJECTED: "bg-red-100 text-red-800 border border-red-200",
    };
    return colors[s] || "bg-gray-100 text-gray-800";
  };

  const getPaymentStatusColor = (status: string) => {
    const s = status ? status.toUpperCase() : "";
    const colors: Record<string, string> = {
      VERIFIED: "bg-emerald-100 text-emerald-800 border border-emerald-200",
      PAID: "bg-green-100 text-green-800 border border-green-200",
      PENDING: "bg-yellow-100 text-yellow-800 border border-yellow-200",
    };
    return colors[s] || "bg-gray-100 text-gray-800";
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 font-sans">
      {loading && <Loader />}

      {stats && (
        <div className="max-w-7xl mx-auto mb-8">
          <header className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-serif font-bold text-slate-900">
                {title}
              </h1>
              <p className="text-slate-500 mt-1">
                Comprehensive overview of {isFaculty ? "faculty" : "non-faculty"} recruitment applications
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(prefix)}
                className="flex items-center gap-2 bg-slate-800 text-white px-4 py-2 rounded shadow hover:bg-slate-900 transition border border-slate-700"
              >
                <Home size={18} />
                Home
              </button>
              <button
                onClick={() =>
                  handleDownload(
                    `${prefix}/application/admin/export/xlsx-stream?recruitmentId=${recruitmentId}`,
                    "applications.xlsx"
                  )
                }
                className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg shadow-sm hover:bg-green-700 transition"
              >
                <Download size={18} />
                Export
              </button>
            </div>
          </header>
          <DashboardStats stats={stats} />
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-serif font-bold text-slate-900 mb-2">
            Applications Management
          </h2>
          <p className="text-slate-600">
            Search, filter, and manage applications
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or application ID..."
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                value={filters.q}
                onChange={(e) => handleFilterChange("q", e.target.value)}
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <Filter className="h-4 w-4" />
              <span>{showFilters ? "Hide" : "Show"} Filters</span>
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-200">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Category</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={filters.categoryOfCandidate}
                  onChange={(e) => handleFilterChange("categoryOfCandidate", e.target.value)}
                >
                  <option value="">All Categories</option>
                  <option value="UR">UR</option>
                  <option value="SC">SC</option>
                  <option value="ST">ST</option>
                  <option value="OBC">OBC</option>
                  <option value="EWS">EWS</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Payment Status</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={filters.paymentStatus}
                  onChange={(e) => handleFilterChange("paymentStatus", e.target.value)}
                >
                  <option value="">All Payments</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Application Status</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="DRAFT">Draft</option>
                  <option value="SUBMITTED">Submitted</option>
                  <option value="ACCEPTED">Accepted</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Department</label>
                <input
                  type="text"
                  placeholder="Department name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={filters.department}
                  onChange={(e) => handleFilterChange("department", e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Candidate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position & Department</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {applications.length > 0 ? (
                  applications.map((app) => (
                    <tr key={app.applicationId} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                              <User className="h-5 w-5 text-white" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{app.personalInfo?.name || "N/A"}</div>
                            <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                              <Mail className="h-3 w-3" />
                              {app.personalInfo?.email || ""}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">{app.personalInfo?.candidateType || "N/A"}</div>
                          <div className="text-gray-500 flex items-center gap-1 mt-1">
                            <Building className="h-3 w-3" />
                            {app.personalInfo?.department || "N/A"}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${getPaymentStatusColor(app.payment?.status)}`}>
                          <DollarSign className="h-3 w-3 mr-1" />
                          {app.payment?.status ? String(app.payment.status).toUpperCase() : "PENDING"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(app.status)}`}>
                          {app.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => navigate(`${prefix}/application/${app.recruitmentId}/${app.applicationId}/view`)}
                            className="text-indigo-600 hover:text-indigo-900 text-sm font-semibold flex items-center gap-1"
                          >
                            <Eye className="h-4 w-4" /> View
                          </button>
                          <span className="text-gray-300">|</span>
                          <button
                            onClick={() => navigate(`${prefix}/admin/application/${app.recruitmentId}/${app.applicationId}/review`)}
                            className="text-emerald-600 hover:text-emerald-900 text-sm font-semibold"
                          >
                            Review
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center py-10 text-gray-500">
                      No applications found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {pagination.totalPages > 0 && (
            <div className="px-6 py-4 bg-slate-50 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <span>Show</span>
                <select
                  value={filters.limit}
                  onChange={(e) => handleFilterChange("limit", Number(e.target.value))}
                  className="px-2.5 py-1 border border-slate-300 rounded-md bg-white text-xs font-semibold text-slate-700 shadow-xs focus:ring-2 focus:ring-slate-900 focus:outline-none cursor-pointer"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>entries per page</span>
                <span className="ml-2 text-slate-500 font-medium">
                  (Total: <strong className="text-slate-800">{pagination.total}</strong> records)
                </span>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 font-medium mr-2">
                  Page <span className="font-bold text-slate-900">{pagination.currentPage}</span> of{" "}
                  <span className="font-bold text-slate-900">{pagination.totalPages}</span>
                </span>

                <button
                  disabled={pagination.currentPage === 1}
                  onClick={() => handleFilterChange("page", 1)}
                  className="px-3 py-1.5 text-xs font-semibold bg-white border border-slate-200 rounded-lg hover:bg-slate-100 text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  First
                </button>
                <button
                  disabled={pagination.currentPage === 1}
                  onClick={() => handleFilterChange("page", pagination.currentPage - 1)}
                  className="px-3.5 py-1.5 text-xs font-semibold bg-white border border-slate-200 rounded-lg hover:bg-slate-100 text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  Previous
                </button>
                <button
                  disabled={pagination.currentPage === pagination.totalPages}
                  onClick={() => handleFilterChange("page", pagination.currentPage + 1)}
                  className="px-3.5 py-1.5 text-xs font-semibold bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  Next
                </button>
                <button
                  disabled={pagination.currentPage === pagination.totalPages}
                  onClick={() => handleFilterChange("page", pagination.totalPages)}
                  className="px-3 py-1.5 text-xs font-semibold bg-white border border-slate-200 rounded-lg hover:bg-slate-100 text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
